import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { bruneiTimeAgo } from '../../lib/brunei';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Loader2, Eye, EyeOff, TestTube, Save, Settings2, Globe } from 'lucide-react';
import EncryptionMigrationCard from './EncryptionMigrationCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function IntegrationsManagement() {
  const [integrations, setIntegrations] = useState([]);
  const [selectedEnvironment, setSelectedEnvironment] = useState('uat');
  const [loading, setLoading] = useState(true);
  const [editingService, setEditingService] = useState(null);
  const [editConfig, setEditConfig] = useState({});
  const [showKeys, setShowKeys] = useState({});
  const [testing, setTesting] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadIntegrations = useCallback(async () => {
    try {
      setLoading(true);
      
      const response = await fetch(
        `${API_URL}/api/admin/integrations?environment=${selectedEnvironment}`,
        { credentials: 'include' }
      );

      if (!response.ok) throw new Error('Failed to load integrations');

      const data = await response.json();
      setIntegrations(data);
    } catch (error) {
      console.error('Error loading integrations:', error);
      toast.error('Failed to load integrations');
    } finally {
      setLoading(false);
    }
  }, [selectedEnvironment]);

  useEffect(() => {
    loadIntegrations();
  }, [loadIntegrations]);

  const handleEdit = async (serviceName) => {
    try {
      const response = await fetch(
        `${API_URL}/api/admin/integrations/${serviceName}?environment=${selectedEnvironment}`,
        { credentials: 'include' }
      );

      if (!response.ok) throw new Error('Failed to load integration details');

      const data = await response.json();
      setEditingService(data);

      // Pre-fill any empty field with its schema-declared default so admins
      // see sensible values on first open instead of blank inputs.
      // Existing values from the saved config always win.
      const existing = data.config || {};
      const merged = { ...existing };
      const fields = data.fields || {};
      Object.entries(fields).forEach(([key, info]) => {
        if (!merged[key] && info?.default) {
          merged[key] = info.default;
        }
      });
      setEditConfig(merged);
      setShowKeys({});
    } catch (error) {
      console.error('Error loading integration:', error);
      toast.error('Failed to load integration details');
    }
  };

  const handleSave = async () => {
    if (!editingService) return;

    try {
      setSaving(true);

      const response = await fetch(
        `${API_URL}/api/admin/integrations/${editingService.service_name}`,
        {
          method: 'PUT',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            environment: selectedEnvironment,
            config: editConfig
          })
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save configuration');
      }

      toast.success('Configuration saved successfully');
      setEditingService(null);
      setEditConfig({});
      loadIntegrations();
    } catch (error) {
      console.error('Error saving integration:', error);
      toast.error(error.message || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async (serviceName) => {
    try {
      setTesting(serviceName);

      const response = await fetch(
        `${API_URL}/api/admin/integrations/${serviceName}/test?environment=${selectedEnvironment}`,
        {
          method: 'POST',
          credentials: 'include',
        }
      );

      if (!response.ok) throw new Error('Test failed');

      const result = await response.json();
      
      if (result.success) {
        toast.success(result.message || 'Connection test successful');
      } else {
        toast.error(result.message || 'Connection test failed');
      }

      loadIntegrations();
    } catch (error) {
      console.error('Error testing integration:', error);
      toast.error('Failed to test connection');
    } finally {
      setTesting(null);
    }
  };

  const toggleShowKey = (field) => {
    setShowKeys(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const getStatusBadge = (integration) => {
    if (integration.test_status === 'success') {
      return <Badge className="bg-green-600">✅ Connected</Badge>;
    } else if (integration.test_status === 'failed') {
      return <Badge variant="destructive">❌ Failed</Badge>;
    } else if (integration.test_status === 'not_configured') {
      return <Badge variant="secondary">⚠️ Not Configured</Badge>;
    } else {
      return <Badge variant="outline">○ Untested</Badge>;
    }
  };

  const formatLastTested = (timestamp) => bruneiTimeAgo(timestamp) || 'Never';

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Integrations Management</h1>
        <p className="text-muted-foreground">
          Configure third-party service credentials and test connections
        </p>
      </div>

      <div className="mb-6 flex justify-between items-center">
        <div className="flex items-center gap-2">
          <Label>Environment:</Label>
          <Select value={selectedEnvironment} onValueChange={setSelectedEnvironment}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="uat">UAT / Testing</SelectItem>
              <SelectItem value="production">Production</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {selectedEnvironment === 'production' && (
          <Badge variant="destructive" className="text-sm">
            ⚠️ Production Environment
          </Badge>
        )}
      </div>

      <div className="grid gap-6">
        {integrations.map((integration) => (
          <Card key={integration.service_name} className="border-2">
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <span className="text-2xl">{integration.service_icon}</span>
                    {integration.service_display_name}
                    {getStatusBadge(integration)}
                  </CardTitle>
                  <CardDescription>{integration.description}</CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleEdit(integration.service_name)}
                  disabled={!integration.is_active && integration.test_status !== 'not_configured'}
                >
                  <Settings2 className="h-4 w-4 mr-2" />
                  {integration.test_status === 'not_configured' ? 'Configure' : 'Edit'}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {integration.config_preview && Object.keys(integration.config_preview).length > 0 ? (
                  <>
                    {Object.entries(integration.config_preview).map(([key, value]) => (
                      <div key={key} className="flex justify-between items-center text-sm">
                        <span className="text-muted-foreground capitalize">
                          {key.replace(/_/g, ' ')}:
                        </span>
                        <span className="font-mono text-xs">{value || 'Not set'}</span>
                      </div>
                    ))}
                    
                    {integration.last_tested_at && (
                      <div className="flex justify-between items-center text-sm pt-2 border-t">
                        <span className="text-muted-foreground">Last tested:</span>
                        <span>{formatLastTested(integration.last_tested_at)}</span>
                      </div>
                    )}

                    <div className="flex gap-2 pt-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleTest(integration.service_name)}
                        disabled={testing === integration.service_name || !integration.is_active}
                        data-testid={`test-btn-${integration.service_name}`}
                      >
                        {testing === integration.service_name ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            {integration.service_name === 'site_settings' ? 'Verifying...' : 'Testing...'}
                          </>
                        ) : (
                          <>
                            {integration.service_name === 'site_settings' ? (
                              <Globe className="h-4 w-4 mr-2" />
                            ) : (
                              <TestTube className="h-4 w-4 mr-2" />
                            )}
                            {integration.service_name === 'site_settings' ? 'Verify URL' : 'Test Connection'}
                          </>
                        )}
                      </Button>
                    </div>
                  </>
                ) : (
                  <p className="text-muted-foreground text-sm">
                    Not configured. Click "Configure" to set up.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="mt-8">
        <EncryptionMigrationCard />
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editingService} onOpenChange={() => setEditingService(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-2xl">{editingService?.icon}</span>
              Edit {editingService?.service_display_name} Configuration
            </DialogTitle>
            <DialogDescription>
              Update credentials for {selectedEnvironment === 'uat' ? 'UAT/Testing' : 'Production'} environment
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {editingService?.fields && Object.entries(editingService.fields).map(([field, fieldInfo]) => (
              <div key={field} className="space-y-2">
                <Label htmlFor={field}>
                  {fieldInfo.label}
                  {fieldInfo.required && <span className="text-red-500 ml-1">*</span>}
                </Label>
                {fieldInfo.type === 'boolean' ? (
                  // Boolean fields render as a toggle. Backend stores them as
                  // string "true"/"false" because the encrypted config dict
                  // is flat strings — we convert on read/write at the edges.
                  <div className="flex items-center gap-3 pt-1">
                    <Switch
                      id={field}
                      checked={(() => {
                        const v = editConfig[field];
                        if (v === undefined || v === null || v === '') {
                          // Fall back to declared default (also a string in
                          // the schema). Default to "on" so a brand-new field
                          // doesn't appear off the first time admin opens it.
                          return String(fieldInfo.default ?? 'true').toLowerCase() === 'true';
                        }
                        return String(v).toLowerCase() === 'true';
                      })()}
                      onCheckedChange={(checked) => setEditConfig({
                        ...editConfig,
                        [field]: checked ? 'true' : 'false',
                      })}
                      data-testid={`integration-toggle-${field}`}
                    />
                    <span className="text-sm text-muted-foreground">
                      {(() => {
                        const v = editConfig[field];
                        const on = (v === undefined || v === null || v === '')
                          ? String(fieldInfo.default ?? 'true').toLowerCase() === 'true'
                          : String(v).toLowerCase() === 'true';
                        return on ? 'Enabled' : 'Disabled';
                      })()}
                    </span>
                  </div>
                ) : (
                  <div className="relative">
                    <Input
                      id={field}
                      type={showKeys[field] ? 'text' : (fieldInfo.type === 'password' ? 'password' : 'text')}
                      placeholder={fieldInfo.default || `Enter ${fieldInfo.label.toLowerCase()}`}
                      value={editConfig[field] || ''}
                      onChange={(e) => setEditConfig({
                        ...editConfig,
                        [field]: e.target.value
                      })}
                      className="pr-10"
                    />
                    {fieldInfo.type === 'password' && (
                      <button
                        type="button"
                        onClick={() => toggleShowKey(field)}
                        className="absolute right-2 top-1/2 -translate-y-1/2"
                      >
                        {showKeys[field] ? (
                          <EyeOff className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <Eye className="h-4 w-4 text-muted-foreground" />
                        )}
                      </button>
                    )}
                  </div>
                )}
                {fieldInfo.help && (
                  <p className="text-xs text-muted-foreground">{fieldInfo.help}</p>
                )}
              </div>
            ))}

            {selectedEnvironment === 'production' && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded">
                <p className="text-sm text-red-700">
                  ⚠️ <strong>Warning:</strong> You are updating <strong>Production</strong> credentials. 
                  Please ensure you have the correct keys before saving.
                </p>
              </div>
            )}

            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
              <p className="text-sm text-blue-700">
                ℹ️ Changes will be applied immediately. This action will be logged in the audit trail.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditingService(null)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
